import * as React from 'react';
import { Loader2, Check, Star } from 'lucide-react';
import { format } from 'date-fns';
import type { ColumnDef } from '@tanstack/react-table';

import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Badge } from '@/components/ui/badge';
import { DataTableEnhanced } from '@/components/ui/data-table-enhanced';
import type { ProjectWithMetadata, ProjectStatus } from '@/types/project-selector';

interface ProjectSearchModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  projects: ProjectWithMetadata[];
  favourites?: ProjectWithMetadata[];
  onToggleFavourite?: (projectId: string) => void;
  value?: string;
  onSelect: (projectId: string) => void;
  // Server-side search props (optional)
  onServerSearch?: (query: string) => void;
  isSearching?: boolean;
}

// Status badge configuration
const statusConfig: Record<
  ProjectStatus,
  { label: string; variant: 'default' | 'secondary' | 'destructive' | 'outline' }
> = {
  active: { label: 'Active', variant: 'default' },
  completed: { label: 'Completed', variant: 'secondary' },
  archived: { label: 'Archived', variant: 'destructive' },
  planning: { label: 'Planning', variant: 'outline' },
  'on-hold': { label: 'On Hold', variant: 'secondary' },
  cancelled: { label: 'Cancelled', variant: 'destructive' },
  pending: { label: 'Pending', variant: 'outline' },
};

export function ProjectSearchModal({
  open,
  onOpenChange,
  projects,
  favourites = [],
  onToggleFavourite,
  value,
  onSelect,
  isSearching,
}: ProjectSearchModalProps) {
  const handleSelect = React.useCallback(
    (projectId: string) => {
      onSelect(projectId);
      onOpenChange(false);
    },
    [onSelect, onOpenChange]
  );

  const favouriteIds = React.useMemo(
    () => new Set(favourites.map((f) => f.id)),
    [favourites]
  );

  const columns = React.useMemo<ColumnDef<ProjectWithMetadata>[]>(() => {
    const cols: ColumnDef<ProjectWithMetadata>[] = [];

    // Selected indicator
    cols.push({
      id: 'selected',
      header: '',
      cell: ({ row }) =>
        value === row.original.id ? (
          <Check className="h-4 w-4 text-primary" />
        ) : null,
      enableSorting: false,
      enableColumnFilter: false,
      enableHiding: false,
      size: 32,
    });

    // Favourite toggle — empty header (no icon), star cell only
    cols.push({
      id: 'favourite',
      header: '',
      cell: ({ row }) => {
        const isFav = favouriteIds.has(row.original.id);
        return (
          <button
            onClick={(e) => {
              e.stopPropagation();
              onToggleFavourite?.(row.original.id);
            }}
            className="flex items-center justify-center"
            aria-label={isFav ? 'Remove from favourites' : 'Add to favourites'}
          >
            <Star
              className={`h-4 w-4 transition-colors ${
                isFav ? 'fill-yellow-400 text-yellow-400' : 'text-muted-foreground'
              }`}
            />
          </button>
        );
      },
      enableSorting: false,
      enableColumnFilter: false,
      enableHiding: false,
      size: 40,
    });

    // Project Code
    cols.push({
      accessorKey: 'code',
      header: 'Project Code',
      size: 120,
    });

    // Project Name
    cols.push({
      accessorKey: 'name',
      header: 'Project Name',
      size: 220,
    });

    // Client Code
    cols.push({
      accessorKey: 'clientCode',
      header: 'Client Code',
      cell: ({ row }) => <span>{row.original.clientCode ?? ''}</span>,
      size: 100,
    });

    // Client Name
    cols.push({
      accessorKey: 'clientName',
      header: 'Client Name',
      cell: ({ row }) => <span>{row.original.clientName}</span>,
      size: 180,
    });

    // Project Manager
    cols.push({
      accessorKey: 'projectManager',
      header: 'Project Manager',
      cell: ({ row }) => <span>{row.original.projectManager ?? ''}</span>,
      size: 160,
    });

    // Status
    cols.push({
      accessorKey: 'status',
      header: 'Status',
      cell: ({ row }) => {
        const cfg = statusConfig[row.original.status] ?? {
          label: row.original.status,
          variant: 'outline' as const,
        };
        return <Badge variant={cfg.variant}>{cfg.label}</Badge>;
      },
      size: 100,
    });

    // Start Date
    cols.push({
      accessorKey: 'startDate',
      header: 'Start Date',
      cell: ({ row }) =>
        row.original.startDate ? (
          <span className="text-sm tabular-nums">
            {format(row.original.startDate, 'dd MMM yyyy')}
          </span>
        ) : (
          <span className="text-muted-foreground">—</span>
        ),
      size: 110,
    });

    // End Date
    cols.push({
      accessorKey: 'endDate',
      header: 'End Date',
      cell: ({ row }) =>
        row.original.endDate ? (
          <span className="text-sm tabular-nums">
            {format(row.original.endDate, 'dd MMM yyyy')}
          </span>
        ) : (
          <span className="text-muted-foreground">—</span>
        ),
      size: 110,
    });

    // Sort Keys
    cols.push({
      id: 'sortKeys',
      header: 'Sort Keys',
      accessorFn: (row) => row.sortKeys?.join(', ') ?? '',
      cell: ({ row }) => {
        const keys = row.original.sortKeys;
        if (!keys || keys.length === 0)
          return <span className="text-muted-foreground">—</span>;
        return (
          <div className="flex flex-wrap gap-1">
            {keys.map((k) => (
              <Badge key={k} variant="outline" className="text-xs px-1.5 py-0">
                {k}
              </Badge>
            ))}
          </div>
        );
      },
      size: 100,
    });

    // Custom Fields
    cols.push({
      id: 'customFields',
      header: 'Custom Fields',
      accessorFn: (row) =>
        row.customFields?.map((f) => `${f.label}: ${f.value}`).join(' | ') ?? '',
      cell: ({ row }) => {
        const fields = row.original.customFields;
        if (!fields || fields.length === 0)
          return <span className="text-muted-foreground">—</span>;
        return (
          <div className="space-y-0.5">
            {fields.map((f) => (
              <div key={f.key} className="flex items-center gap-1 text-xs">
                <span className="text-muted-foreground shrink-0">{f.label}:</span>
                <span className="font-medium truncate">{f.value}</span>
              </div>
            ))}
          </div>
        );
      },
      size: 180,
      enableSorting: false,
    });

    return cols;
  }, [value, handleSelect, favouriteIds, onToggleFavourite]);

  const fieldLabels: Record<string, string> = {
    selected: '',
    favourite: '',
    code: 'Project Code',
    name: 'Project Name',
    clientCode: 'Client Code',
    clientName: 'Client Name',
    projectManager: 'Project Manager',
    status: 'Status',
    startDate: 'Start Date',
    endDate: 'End Date',
    sortKeys: 'Sort Keys',
    customFields: 'Custom Fields',
  };

  const fieldCategories: Record<string, string> = {
    code: 'Project',
    name: 'Project',
    clientCode: 'Client',
    clientName: 'Client',
    projectManager: 'Project',
    status: 'Project',
    startDate: 'Dates',
    endDate: 'Dates',
    sortKeys: 'Additional',
    customFields: 'Additional',
  };

  const initialColumnVisibility: Record<string, boolean> = {
    selected: true,
    favourite: true,
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-[95vw] w-[1200px] h-[85vh] max-h-[900px] flex flex-col p-0 gap-0 overflow-hidden">
        <DialogHeader className="px-6 py-4 border-b shrink-0">
          <DialogTitle className="flex items-center gap-2">
            Select Project
            {isSearching && <Loader2 className="h-4 w-4 animate-spin text-muted-foreground" />}
          </DialogTitle>
        </DialogHeader>

        <div className="flex-1 min-h-0 overflow-hidden px-6 py-4">
          <DataTableEnhanced
            columns={columns}
            data={projects}
            searchKey="name"
            searchPlaceholder="Search by code, name, client, or PM..."
            enableSorting
            enableFiltering
            enableColumnVisibility
            enableGrouping={false}
            enableRowSelection={false}
            pageSize={20}
            storageKey="project-search-modal"
            fieldLabels={fieldLabels}
            fieldCategories={fieldCategories}
            initialColumnVisibility={initialColumnVisibility}
            showSortIconsWhenNotSorted
            onRowClick={(row) => handleSelect(row.id)}
          />
        </div>
      </DialogContent>
    </Dialog>
  );
}
